// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsDutyGetAllAttrs,
  IAboutUsDutyGetAttrs,
  IAboutUsDutySaveAttrs,
} from 'interfaces/back-office/content-management/about-us/duty/about-us-duty-view.interface'
import {
  IAboutUsDutyApiGetAllResponse,
  IAboutUsDutyApiGetResponse,
  IAboutUsDutyApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/duty/about-us-duty-api.interface'
import { IAboutUsDutyDetail } from 'interfaces/back-office/content-management/about-us/duty'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

const aboutUsDutyContentProps = {
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
}

// About Us Duty Props
const aboutUsDutyProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  content: types.maybeNull(types.array(types.model('AboutUsDutyContent').props(aboutUsDutyContentProps))),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// About Us Duty List Model
const AboutUsDuties = types.array(
  types.model('AboutUsDuties').props({
    ...aboutUsDutyProps,
    aboutUsDuties: types.maybeNull(types.array(types.model('AboutUsDuties').props(aboutUsDutyProps))),
  }),
)

// About Us Duty Detail Model
const AboutUsDutyDetail = types.model('AboutUsDutyDetail').props(aboutUsDutyProps)

const AboutUsDutyStoreModel = types
  .model('AboutUsDuty')
  .props({
    aboutUsDuty_isLoading: types.optional(types.boolean, false),
    aboutUsDuty_isSaveLoading: types.optional(types.boolean, false),
    aboutUsDuty_aboutUsDuties: types.optional(AboutUsDuties, []),
    aboutUsDuty_detail: types.optional(AboutUsDutyDetail, {}),
  })
  .actions(self => ({
    aboutUsDuty_SET_LOADING: (payload: boolean): void => {
      self.aboutUsDuty_isLoading = payload
    },
    aboutUsDuty_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsDuty_isSaveLoading = payload
    },
    aboutUsDuty_SET_ABOUT_US_DUTIES: (payload: IAboutUsDutyDetail[]): void => {
      self.aboutUsDuty_aboutUsDuties = cast(payload?.map((list, index) => ({ ...list, number: index + 1 })) || [])
    },
    aboutUsDuty_SET_ABOUT_US_DUTY: (payload: IAboutUsDutyDetail): void => {
      self.aboutUsDuty_detail = cast(payload)
    },
    aboutUsDuty_CLEAR_ABOUT_US_DUTIES: (): void => {
      self.aboutUsDuty_aboutUsDuties = cast([])
    },
    aboutUsDuty_CLEAR_ABOUT_US_DUTY: (): void => {
      self.aboutUsDuty_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about Us Duties
     *
     * @param {IAboutUsDutyGetAllAttrs} payload
     *
     * @return {any} any
     */
    aboutUsDuty_fetchAboutUsDuties: flow(function* (payload: IAboutUsDutyGetAllAttrs) {
      self.aboutUsDuty_SET_LOADING(true)

      const response: IAboutUsDutyApiGetAllResponse = yield self.environment.aboutUsDutyApi.aboutUsDuty_getAboutUsDuties({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.aboutUsDuty_SET_ABOUT_US_DUTIES(response.res.data)

      self.aboutUsDuty_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us duty
     *
     * @param {IAboutUsDutyGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsDuty_fetchAboutUsDuty: flow(function* (payload: IAboutUsDutyGetAttrs) {
      self.aboutUsDuty_SET_LOADING(true)

      const response: IAboutUsDutyApiGetResponse = yield self.environment.aboutUsDutyApi.aboutUsDuty_getAboutUsDuty({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.aboutUsDuty_SET_ABOUT_US_DUTY(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsDuty_SET_LOADING(false)
    }),

    /**
     * @description Save about us duty
     *
     * @param {IAboutUsDutySaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsDuty_saveAboutUsDuty: flow(function* (payload: IAboutUsDutySaveAttrs) {
      self.aboutUsDuty_SET_SAVE_LOADING(true)

      const response: IAboutUsDutyApiSaveResponse = yield self.environment.aboutUsDutyApi.aboutUsDuty_saveAboutUsDuty({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsDuty_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsDutyStoreDefaultModel = () => types.optional(AboutUsDutyStoreModel, {})
